"use client"

import { useEffect, useRef, useState } from "react"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import BannerImage from "@/components/banners/banner-image"
import BannerLink from "@/components/banners/banner-link"
import { BANNER_AUTOPLAY_DELAY, type SiteBanner } from "@/lib/banners"
import styles from "./brands-banner-carousel.module.css"

export const brandsBannerOptions = {
  align: "start",
  containScroll: false,
  loop: true,
  slidesToScroll: 1,
  duration: 25,
} as const

export default function BrandsBannerCarousel({
  banners,
  userId,
}: {
  banners: SiteBanner[]
  userId?: string
}) {
  const count = banners.length
  // Two originals cannot fill both sides of the loop while the next slide peeks in.
  const slides = count === 2 ? [...banners, ...banners] : banners
  const [selectedSnap, setSelectedSnap] = useState(0)
  const autoplay = useRef(
    Autoplay({
      delay: BANNER_AUTOPLAY_DELAY,
      playOnInit: count > 1,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
      rootNode: (root) => root.parentElement || root,
      breakpoints: {
        "(prefers-reduced-motion: reduce)": { active: false },
      },
    })
  )
  const [carouselRef, carouselApi] = useEmblaCarousel(
    { ...brandsBannerOptions, loop: count > 1, watchDrag: count > 1 },
    [autoplay.current]
  )

  useEffect(() => {
    if (!carouselApi) return
    const updateSelection = () =>
      setSelectedSnap(carouselApi.selectedScrollSnap())
    updateSelection()
    carouselApi.on("select", updateSelection)
    carouselApi.on("reInit", updateSelection)
    return () => {
      carouselApi.off("select", updateSelection)
      carouselApi.off("reInit", updateSelection)
    }
  }, [carouselApi])

  if (!count) return null

  const activeIndex = selectedSnap % count
  const selectBanner = (index: number) => {
    if (index === activeIndex) return
    if (count === 2) carouselApi?.scrollNext()
    else carouselApi?.scrollTo(index)
  }

  return (
    <section
      aria-label="Brands banners"
      aria-roledescription="carousel"
      data-active-banner={banners[activeIndex]?.id}
      className={`group/brands-banner ${styles.carousel} ${count === 1 ? styles.single : ""}`}
      onFocusCapture={() => autoplay.current.stop()}
      onBlurCapture={(event) => {
        if (
          carouselApi &&
          count > 1 &&
          !event.currentTarget.contains(event.relatedTarget) &&
          !event.currentTarget.matches(":hover") &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          autoplay.current.play()
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault()
          if (event.key === "ArrowLeft") carouselApi?.scrollPrev()
          else carouselApi?.scrollNext()
        }
      }}
    >
      <div
        ref={carouselRef}
        tabIndex={count > 1 ? 0 : undefined}
        className={`${styles.viewport} rounded-[8px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0F9E4F]`}
      >
        <div className={styles.track} aria-live="off">
          {slides.map((banner, renderIndex) => {
            const sourceIndex = renderIndex % count
            const active = renderIndex === selectedSnap
            const eager = [
              selectedSnap,
              (selectedSnap + 1) % slides.length,
              (selectedSnap + slides.length - 1) % slides.length,
            ].includes(renderIndex)
            return (
              <div
                key={`${banner.id}-${renderIndex}`}
                data-brand-banner-slide={renderIndex}
                role="group"
                aria-roledescription="slide"
                aria-label={`${sourceIndex + 1} of ${count}`}
                aria-hidden={!active}
                inert={!active}
                className={styles.slide}
                style={{ opacity: active ? 1 : 0.45 }}
              >
                <BannerLink
                  banner={banner}
                  index={sourceIndex}
                  active={active}
                  userId={userId}
                >
                  <BannerImage
                    banner={banner}
                    alt={`Featured brand offer ${sourceIndex + 1}`}
                    sizes="(max-width: 639px) 321px, (max-width: 1439px) 75vw, 998px"
                    mobileSizes="321px"
                    priority={renderIndex === 0}
                    loading={
                      renderIndex === 0 ? undefined : eager ? "eager" : "lazy"
                    }
                    className="object-cover"
                  />
                </BannerLink>
              </div>
            )
          })}
        </div>
      </div>
      {count > 1 && (
        <div className={styles.controls}>
          <div className="pointer-events-auto absolute right-2 bottom-1 flex flex-wrap items-center justify-end gap-0 sm:right-3 sm:bottom-2">
            {banners.map((banner, index) => (
              <button
                key={banner.id}
                type="button"
                aria-label={`Go to brand banner ${index + 1}`}
                aria-current={activeIndex === index ? "true" : undefined}
                title={`Go to banner ${index + 1}`}
                onClick={() => selectBanner(index)}
                className="flex size-4 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white"
              >
                <span
                  aria-hidden="true"
                  className={`size-[4px] rounded-full ${activeIndex === index ? "bg-white" : "bg-white/40"}`}
                />
              </button>
            ))}
          </div>
          <button
            type="button"
            aria-label="Previous brand banner"
            title="Previous banner"
            onClick={() => carouselApi?.scrollPrev()}
            className="pointer-events-auto absolute top-1/2 left-3 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1D2129] opacity-0 transition-opacity group-focus-within/brands-banner:opacity-100 group-hover/brands-banner:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F9E4F] sm:flex"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Next brand banner"
            title="Next banner"
            onClick={() => carouselApi?.scrollNext()}
            className="pointer-events-auto absolute top-1/2 right-3 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1D2129] opacity-0 transition-opacity group-focus-within/brands-banner:opacity-100 group-hover/brands-banner:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F9E4F] sm:flex"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </section>
  )
}
